import { htmlDecode } from '~/helpers/htmlDecoder';
import { ProductStockStatus } from '~/modules/GraphQL/types';

const DEFAULT_CURRENCY = 'SEK';
const BRAND_NAME = 'Blodkollen';

export type ProductJsonLdSource = {
  name?: string | null;
  sku?: string | null;
  meta_description?: string | null;
  short_description?: { html?: string | null } | null;
  image?: { url?: string | null } | null;
  stock_status?: string | null;
  price_range?: {
    minimum_price?: {
      final_price?: {
        value?: number | null;
        currency?: string | null;
      } | null;
    } | null;
  } | null;
};

type ProductJsonLdOptions = {
  url?: string | null;
};

export type ProductJsonLd = {
  '@context': string;
  '@type': string;
  name: string;
  description?: string;
  image?: string;
  sku?: string;
  url?: string;
  brand: {
    '@type': string;
    name: string;
  };
  offers?: {
    '@type': string;
    url?: string;
    price: number;
    priceCurrency: string;
    availability: string;
  };
};

const toPlainText = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  return htmlDecode(value)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const buildProductJsonLd = (
  product?: ProductJsonLdSource | null,
  { url }: ProductJsonLdOptions = {},
): ProductJsonLd | null => {
  const name = toPlainText(product?.name);
  if (!product || !name) {
    return null;
  }

  const description = toPlainText(product.meta_description) || toPlainText(product.short_description?.html);
  const image = product.image?.url || undefined;
  const sku = product.sku || undefined;
  const pageUrl = url || undefined;
  const jsonLd: ProductJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    brand: {
      '@type': 'Brand',
      name: BRAND_NAME,
    },
  };

  if (description) {
    jsonLd.description = description;
  }

  if (image) {
    jsonLd.image = image;
  }

  if (sku) {
    jsonLd.sku = sku;
  }

  if (pageUrl) {
    jsonLd.url = pageUrl;
  }

  const price = product.price_range?.minimum_price?.final_price?.value;
  if (product.price_range && typeof price === 'number') {
    jsonLd.offers = {
      '@type': 'Offer',
      price,
      priceCurrency: product.price_range.minimum_price?.final_price?.currency || DEFAULT_CURRENCY,
      availability:
        product.stock_status === ProductStockStatus.InStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    };

    if (pageUrl) {
      jsonLd.offers.url = pageUrl;
    }
  }

  return jsonLd;
};
